import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import {
    faAngleLeft,
    faAngleUp,
    faArrowDown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Stack,
    useTheme,
} from "@vapor/react-material";
import dayjs from "dayjs";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FulfilmentDto } from "../../../../../core/api/use-get-fulfilments/FulfilmentDto";
import { useGetLinkableFulfilments } from "../../../../../core/api/use-get-linkable-fulfilments/useGetLinkableFulfilments";
import { groupFulfilmentsByDeadline } from "../fulfilment-list/groupFulfilmentsByDeadline";
import { FulfilmentUnlinked } from "../fulfilment-unlinked/FulfilmentUnlinked";

const ITEMS_PER_PAGE = 5;

interface FulfilmentListUnlinkedProps {
    onClickLinkTaxPayersToFulfilment: (fulfilment: FulfilmentDto) => unknown;
}

export const FulfilmentListUnlinked = ({
    onClickLinkTaxPayersToFulfilment,
}: FulfilmentListUnlinkedProps): ReactElement | null => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { data: fulfilments } = useGetLinkableFulfilments(false);

    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

    const groupedFulfilmentsByDeadline = useMemo(
        () => (fulfilments ? groupFulfilmentsByDeadline(fulfilments) : []),
        [fulfilments],
    );

    const [page, setPage] = useState(0);
    const maxPage = useMemo(
        () => Math.ceil(groupedFulfilmentsByDeadline.length / ITEMS_PER_PAGE),
        [groupedFulfilmentsByDeadline],
    );

    const handleNext = () => {
        setPage((old) => Math.min(old + 1, maxPage));
    };

    const displayedFulfilments = useMemo(
        () =>
            groupedFulfilmentsByDeadline.slice(0, (page + 1) * ITEMS_PER_PAGE),
        [groupedFulfilmentsByDeadline, page],
    );

    if (!fulfilments) return null;

    return (
        <Accordion
            disableGutters
            expanded={isAccordionExpanded}
            onChange={(_, newExpanded) => setIsAccordionExpanded(newExpanded)}
            sx={{
                boxShadow: "none",
                "&::before": {
                    display: "none",
                },
            }}
        >
            <AccordionSummary
                expandIcon={
                    <FontAwesomeIcon
                        icon={isAccordionExpanded ? faAngleUp : faAngleLeft}
                    />
                }
                sx={{ marginBottom: 2 }}
            >
                <Typography
                    variant="body700"
                    color={theme.palette.primary.interactiveDefault}
                >
                    {t("views.home.accordion.non_linked_fulfilments.label")}
                </Typography>
                <Stack direction="row" spacing={2}>
                    <Typography variant="body">
                        {t(
                            "views.home.accordion.non_linked_fulfilments.action",
                        )}
                    </Typography>
                    <FontAwesomeIcon
                        color={theme.palette.primary.interactiveDefault}
                        icon={faTriangleExclamation}
                    />
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    "&.MuiAccordionDetails-root": {
                        padding: 0,
                    },
                }}
            >
                <Stack>
                    {displayedFulfilments.map((group) => (
                        <Box
                            key={group.deadline.toISOString()}
                            sx={{ marginBottom: 2 }}
                        >
                            {group.fulfilments.map((fulfilment, index) => (
                                <Box key={index}>
                                    <FulfilmentUnlinked
                                        date={dayjs(fulfilment.deadline)}
                                        description={fulfilment.description}
                                        isCustomFulfilment={false}
                                        key={fulfilment.levyId}
                                        levyId={fulfilment.levyId}
                                        onClickLinkTaxPayers={() => {
                                            onClickLinkTaxPayersToFulfilment(
                                                fulfilment,
                                            );
                                        }}
                                        title={fulfilment.title}
                                        header={index === 0}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ))}
                    {page < maxPage && (
                        <Button
                            disabled={
                                page ===
                                groupedFulfilmentsByDeadline.length /
                                    ITEMS_PER_PAGE
                            }
                            onClick={handleNext}
                            sx={{ placeSelf: "center" }}
                            variant="text"
                        >
                            <FontAwesomeIcon
                                icon={faArrowDown}
                                color={theme.palette.primary.interactiveDefault}
                            />
                            <Typography
                                color={theme.palette.primary.interactiveDefault}
                                ml={1}
                            >
                                {t(
                                    "views.home.components.fulfilment_list_not_linked.load_more",
                                    "Carica altri",
                                )}
                            </Typography>
                        </Button>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
